import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { PagePrefix } from '@shared/core';
import type { ApolloError } from '@apollo/client';
import type { PartialUtmParameters, WritableUtmParameters } from '@shared/utils/utmParameters';

const category = 'cashFunds';
const guestPagePrefix: PagePrefix = 'guest';
const action = 'ButtonInteracted';

const cashFundsGuestTelemetry = createTelemetryObject({
  pages: {
    cashFundGuest: (cashFundId: string, utm?: PartialUtmParameters) => ({
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      extraPageInfo: {
        cashFundId,
        ...utm,
      },
    }),
    cashFundConfirmation: (contributionId: string) => ({
      category,
      pagePrefix: guestPagePrefix,
      page: 'confirmation',
      extraPageInfo: {
        contributionId,
      },
    }),
  },
  actions: {
    cashFundWidgetClicked: (cashFundId: string, pageSlug?: string, utm?: WritableUtmParameters) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'website',
      label: 'CashFundWidget',
      extraInfo: {
        name: 'cashFundWidgetClicked',
        cashFundId,
        pageSlug,
        ...utm,
      },
    }),
    expressCheckoutClicked: (cashFundId: string, contributionAmountInMinorUnits: number, currencyCode: string) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      label: 'ExpressCheckout',
      extraInfo: {
        cashFundId,
        contributionAmountInMinorUnits,
        currencyCode,
      },
    }),
    contributeAnotherWayClicked: (cashFundId: string, contributionAmountInMinorUnits: number, currencyCode: string) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      label: 'Contribute Another Way',
      extraInfo: {
        cashFundId,
        contributionAmountInMinorUnits,
        currencyCode,
      },
    }),
    continueToCheckoutClicked: (cashFundId: string, contributionAmountInMinorUnits: number, contributorEmail: string, contributorNote: string, currencyCode: string) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      label: 'Continue to Checkout',
      extraInfo: {
        cashFundId,
        contributionAmountInMinorUnits,
        contributorEmail,
        contributorNote,
        currencyCode,
      },
    }),
    predefinedAmountSelected: (cashFundId: string, amount: number, currencyCode: string) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      label: 'PredefinedAmountButton',
      extraInfo: {
        cashFundId,
        amount,
        currencyCode,
      },
    }),
    confirmationNextStepClicked: (linkName: string, contributionId: string) => ({
      action,
      category,
      pagePrefix: guestPagePrefix,
      page: 'confirmation',
      label: linkName,
      extraInfo: {
        contributionId,
      },
    }),
    queryError: (queryName: string, error: ApolloError | Error, cashFundId?: string) => ({
      action: 'QueryError',
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      extraInfo: {
        operationType: 'query',
        operationName: queryName,
        error: error.message,
        cashFundId,
      },
    }),
    mutationError: (mutationName: string, error: ApolloError | Error, cashFundId?: string) => ({
      action: 'MutationError',
      category,
      pagePrefix: guestPagePrefix,
      page: 'contribute',
      extraInfo: {
        operationType: 'mutation',
        operationName: mutationName,
        error: error.message,
        cashFundId,
      },
    }),
  },
});

const { TelemetryProvider: CashFundsGuestTelemetryProvider, useTelemetry: useCashFundsGuestTelemetry } = createTelemetry(cashFundsGuestTelemetry, {
  eventId: '',
  section: '',
});

export { CashFundsGuestTelemetryProvider, useCashFundsGuestTelemetry };
// Export with legacy name for backward compatibility during migration
export { useCashFundsGuestTelemetry as useCashFundsTelemetry, CashFundsGuestTelemetryProvider as CashFundsTelemetryProvider };
